/**
 * WebSocket Event Central Handler
 * 
 * Processes incoming socket messages with request-response pattern.
 * 
 * Request Structure:
 * {
 *   messageId: string (unique identifier),
 *   messageType: string,
 *   [messageType]: { ... payload ... }
 * }
 * 
 * Response Structure:
 * {
 *   messageId: string (unique for response),
 *   messageIdReference: string (references request messageId),
 *   Status: { status: string, description: string, statusCode: number },
 *   messageType: string,
 *   [messageType]: { ... payload ... }
 * }
 */

import { generateID } from '../utils/generateID';
import { logger } from '../utils/logger';
import { MessageSegmentAssembler, type MessageSegment } from '../utils/messageSequence';
import type {
	EventHandlers,
	ISocket,
	MessageRoute,
	RequestMessage,
	ResponseMessage,
} from './eventCentral.types';
import { socketService } from './socketService';

/** messageType used by the backend for 32KB-segmented replies (AL-311). */
const SEGMENT_MESSAGE_TYPE = 'MessageSegment32KB';

/**
 * Fresh timeout window applied while a multi-segment reply is in flight.
 * Re-armed on every segment so a large-but-steady stream is not killed by the
 * original 30s request timeout, while a stalled stream still fails eventually.
 */
const SEGMENT_REASSEMBLY_TIMEOUT = 30000;

// Re-export all types for consumers
export type { BaseMessage, EventHandlers, ISocket, MessageType, RequestMessage, ResponseMessage, StatusInfo } from './eventCentral.types';

/**
 * Message routing map — maps messageType to forwarding/handling config.
 * Keeps processMessage() lean by replacing the large switch/case block.
 */
const MESSAGE_ROUTES: Record<string, MessageRoute> = {
	// Responses forwarded via socketService
	QueryDocumentResponse: { forward: 'document:response', handler: 'handleDocumentResponse' },
	DocumentResponse: { forward: 'document:response', handler: 'handleDocumentResponse' },
	QueryDashboardResponse: { forward: 'dashboard:response' },
	QueryCasesResponse: { forward: 'cdd-records:response' },
	QueryC2InvitesResponse: { forward: 'invitations:response' },
	QueryMyAccountResponse: { forward: 'myaccount:response' },
	RiskAssessmentResponse: { forward: 'risk-assessment:response' },
	C2InvitationResponse: { forward: 'c2-invitation:response' },
	RechargeResponse: { forward: 'recharge:response' },
	QueryInvoicesResponse: { forward: 'invoices:response' },
	QueryUsageLedgerResponse: { forward: 'usage-ledger:response' },
	CaseReportResponse: { forward: 'c2profile:response' },

	// Credit notification — extract nested payload
	CreditNotification: { forward: 'credit:notification', payloadKey: 'CreditNotification' },

	// Real-time push updates
	CDDRecordUpdated: { forward: 'CDDRecordUpdated' },
	RecordStatusChanged: { forward: 'RecordStatusChanged' },
	InvitationUpdated: { forward: 'InvitationUpdated' },
	InvitationCreated: { forward: 'InvitationCreated', payloadKey: 'InvitationCreated' },
	RiskAssessmentCreated: { forward: 'RiskAssessmentCreated', payloadKey: 'RiskAssessmentCreated' },
	DashboardUpdated: { forward: 'DashboardUpdated' },

	// Messages handled by dedicated EventCentral methods
	AuthConfirmation: { handler: 'handleAuthConfirmation' },
	NotifyOnboardingStageEvent: { handler: 'handleOnboardingStageEvent' },
	SystemNotification: { handler: 'handleSystemNotification' },
	Error: { handler: 'handleErrorMessage' },
};

/**
 * Event Central Service
 * Processes incoming websocket messages with request-response pattern
 * Maps responses to pending requests using messageIdReference
 */
export class EventCentral {
	private handlers: EventHandlers = {};
	private pendingRequests: Map<string, { resolve: (data: any) => void; reject: (error: any) => void; timeout: NodeJS.Timeout }> = new Map();
	private segmentAssembler = new MessageSegmentAssembler();

	/**
	 * Register event handlers
	 */
	registerHandlers(handlers: EventHandlers): void {
		this.handlers = { ...this.handlers, ...handlers };
		logger.debug('[EventCentral] Handlers registered', {
			count: Object.keys(handlers).length
		});
	}

	/**
	 * Send a request and wait for response
	 * Returns a promise that resolves when response arrives
	 */
	sendRequest(
		socket: ISocket,
		messageType: string,
		payload: any,
		timeout: number = 30000
	): Promise<ResponseMessage> {
		return new Promise((resolve, reject) => {
			// Validate socket
			if (!socket || (typeof socket.send !== 'function' && typeof socket.emit !== 'function')) {
				reject(new Error('Invalid socket: must have either send() or emit() method'));
				return;
			}

			// Generate unique messageId
			const messageId = generateID();

			// Create request message
			const request: RequestMessage = {
				messageId,
				messageType,
				messageTimestamp: new Date().toISOString(),
				[messageType]: payload,
			};

			logger.debug('[EventCentral] Sending request', {
				messageId,
				messageType,
				request: JSON.stringify(request),
			});

			// Set up timeout for request
			const timeoutHandle = setTimeout(() => {
				this.pendingRequests.delete(messageId);
				reject(new Error(`Request ${messageId} timed out after ${timeout}ms`));
			}, timeout);

			// Store promise handler for when response arrives
			this.pendingRequests.set(messageId, {
				resolve,
				reject,
				timeout: timeoutHandle,
			});

			// Send request via socket
			// Prefer send() method (SocketService) over emit() (socket.io)
			if (socket.send && typeof socket.send === 'function') {
				socket.send(request);
			} else if (socket.emit && typeof socket.emit === 'function') {
				socket.emit('message', request);
			} else {
				reject(new Error('Socket must implement either send() or emit() method'));
			}
		});
	}

	/**
	 * Process incoming socket message
	 * This is the main entry point for all incoming websocket messages
	 */
	processMessage(rawData: string | ResponseMessage): void {
		try {
			let message: ResponseMessage;

			// Parse if string, otherwise use as-is
			if (typeof rawData === 'string') {
				message = JSON.parse(rawData);
			} else {
				message = rawData;
			}

			logger.debug('[EventCentral] Processing message', {
				messageType: message.messageType,
				messageId: message.messageId,
				messageIdReference: message.messageIdReference,
			});

			// Intercept segmented replies BEFORE the pendingRequests match.
			// Otherwise the first segment would match the pending request and
			// resolve it with segment 1-of-N (silent truncation — AL-311).
			if (message.messageType === SEGMENT_MESSAGE_TYPE) {
				this.handleSegment(message);
				return;
			}

			// Check if this is a response to a pending request
			if (message.messageIdReference && this.pendingRequests.has(message.messageIdReference)) {
				this.handlePendingResponse(message);
				return;
			}

			// Route via MESSAGE_ROUTES map
			const route = MESSAGE_ROUTES[message.messageType];
			if (route) {
				// Forward to socketService if configured
				if (route.forward) {
					const payload = route.payloadKey ? message[route.payloadKey] : message.data ?? message;
					socketService.forwardToHandlers(route.forward, payload);
				}
				// Call dedicated handler method if configured
				if (route.handler && typeof (this as any)[route.handler] === 'function') {
					(this as any)[route.handler](message);
				}
			} else {
				logger.warn('[EventCentral] Unknown message type', {
					messageType: message.messageType
				});
			}
		} catch (error) {
			logger.error('[EventCentral] Error processing message', {
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	/**
	 * Handle response to a pending request
	 */
	private handlePendingResponse(message: ResponseMessage): void {
		const messageIdReference = message.messageIdReference!;
		const pending = this.pendingRequests.get(messageIdReference);

		if (!pending) {
			logger.warn('[EventCentral] No pending request for', { messageIdReference });
			return;
		}

		// Clear timeout
		clearTimeout(pending.timeout);

		// Check response status
		if (message.Status.status === 'OK') {
			logger.info('[EventCentral] Request successful', {
				messageIdReference,
				messageType: message.messageType,
			});
			pending.resolve(message);
		} else {
			logger.error('[EventCentral] Request failed', {
				messageIdReference,
				status: message.Status.status,
				description: message.Status.description,
			});
			pending.reject(new Error(message.Status.description));
		}

		// Remove from pending
		this.pendingRequests.delete(messageIdReference);
	}

	/**
	 * Handle a single `MessageSegment32KB` envelope (AL-311).
	 *
	 * Accumulates segments keyed by `messageIdReference` and, once every part
	 * has arrived, reassembles the original JSON payload and re-enters
	 * processMessage() so the normal pending-request match and MESSAGE_ROUTES
	 * dispatch work unchanged.
	 */
	private handleSegment(message: ResponseMessage): void {
		const reference = message.messageIdReference;
		if (!reference) {
			logger.warn('[EventCentral] Segment received with no messageIdReference');
			return;
		}

		// Server error path: segmentBase64Message32KB emits a single failure
		// segment (e.g. TEMPORARYFAILURE / statusCode 8999) on its own internal
		// error. Reject the pending request rather than waiting forever for
		// parts that will never arrive.
		if (message.Status && message.Status.status !== 'OK') {
			logger.error('[EventCentral] Segmented response failed', {
				reference,
				status: message.Status.status,
				description: message.Status.description,
			});
			this.segmentAssembler.evict(reference);
			this.rejectPending(reference, new Error(message.Status.description || 'Segmented response failed'));
			return;
		}

		const segment = message[SEGMENT_MESSAGE_TYPE] as MessageSegment | undefined;
		if (!segment) {
			logger.warn('[EventCentral] MessageSegment32KB envelope missing payload', { reference });
			return;
		}

		// Re-arm the pending request timeout so a large, steadily-arriving reply
		// is not killed by the original request timeout, while a stalled stream
		// still fails (and its partial accumulator is evicted).
		this.rearmSegmentTimeout(reference);

		const complete = this.segmentAssembler.add(reference, segment);
		logger.debug('[EventCentral] Segment buffered', {
			reference,
			part: segment.part,
			parts: segment.parts,
			complete,
		});

		if (!complete) {
			return;
		}

		const reassembled = this.segmentAssembler.assemble(reference);
		this.segmentAssembler.evict(reference);

		if (reassembled === null) {
			logger.error('[EventCentral] Segment reassembly incomplete despite completion signal', { reference });
			this.rejectPending(reference, new Error('Segmented response reassembly failed'));
			return;
		}

		let fullMessage: ResponseMessage;
		try {
			fullMessage = JSON.parse(reassembled);
		} catch (error) {
			logger.error('[EventCentral] Failed to parse reassembled segmented message', {
				reference,
				error: error instanceof Error ? error.message : String(error),
			});
			this.rejectPending(reference, new Error('Failed to parse reassembled segmented response'));
			return;
		}

		logger.info('[EventCentral] Segmented response reassembled', {
			reference,
			messageType: fullMessage.messageType,
		});

		// Re-enter the normal flow with the fully reassembled message.
		this.processMessage(fullMessage);
	}

	/**
	 * Re-arm the timeout for a pending request while its segmented reply is in
	 * flight. On expiry both the pending request and its partial accumulator
	 * are cleaned up.
	 */
	private rearmSegmentTimeout(reference: string): void {
		const pending = this.pendingRequests.get(reference);
		if (!pending) {
			return;
		}
		clearTimeout(pending.timeout);
		pending.timeout = setTimeout(() => {
			this.segmentAssembler.evict(reference);
			this.pendingRequests.delete(reference);
			pending.reject(new Error(`Request ${reference} timed out during segment reassembly`));
		}, SEGMENT_REASSEMBLY_TIMEOUT);
	}

	/**
	 * Reject and clean up a pending request by reference, if one exists.
	 */
	private rejectPending(reference: string, error: Error): void {
		const pending = this.pendingRequests.get(reference);
		if (!pending) {
			return;
		}
		clearTimeout(pending.timeout);
		pending.reject(error);
		this.pendingRequests.delete(reference);
	}

	/**
	 * Drop all buffered segment accumulators. Called on socket close so a
	 * dropped final segment does not leak its partial string for the session.
	 */
	clearSegmentBuffers(): void {
		this.segmentAssembler.clear();
	}

	/**
	 * Handle Authentication Confirmation
	 */
	private handleAuthConfirmation(message: ResponseMessage): void {
		const data = message.AuthConfirmation;
		if (!data) {
			logger.warn('[EventCentral] AuthConfirmation received with no data');
			return;
		}

		logger.info('[EventCentral] Auth confirmation received', {
			nameLabelDisplay: data.nameLabelDisplay,
			orgLabelDisplay: data.orgLabelDisplay,
		});

		// Invoke registered handler
		this.handlers.onAuthConfirmation?.(data, message.Status);
	}

	/**
	 * Handle Onboarding Stage Update
	 */
	private handleOnboardingStageEvent(message: ResponseMessage): void {
		const data = message.NotifyOnboardingStageEvent;
		if (!data) {
			logger.warn('[EventCentral] OnboardingStageEvent received with no data');
			return;
		}

		const stageType = data.currentOnboardingStage?.onboardingStageType;
		logger.info('[EventCentral] Onboarding stage update', {
			stage: stageType,
			status: data.currentOnboardingStage?.status
		});

		// Invoke registered handler
		this.handlers.onOnboardingStageUpdate?.(data, message.Status);
	}

	/**
	 * Handle Document Query Response
	 */
	private handleDocumentResponse(message: ResponseMessage): void {
		const data = message.QueryDocumentResponse;
		if (!data) {
			logger.warn('[EventCentral] DocumentResponse received with no data');
			return;
		}

		logger.info('[EventCentral] Document received', {
			documentId: data.documentId,
			fileName: data.fileName,
			status: data.status,
		});

		// Store document in session storage
		if (data.data) {
			sessionStorage.setItem('pdf', data.data);
			if (data.fileName) {
				sessionStorage.setItem('pdfFileName', data.fileName);
			}
		}

		// Invoke registered handler
		this.handlers.onDocumentResponse?.(data, message.Status);
	}

	/**
	 * Handle System Notifications
	 */
	private handleSystemNotification(message: ResponseMessage): void {
		const data = message.SystemNotification;
		if (!data) {
			logger.warn('[EventCentral] SystemNotification received with no data');
			return;
		}

		logger.info('[EventCentral] System notification', {
			type: data.type,
			title: data.title
		});

		// Invoke registered handler
		this.handlers.onSystemNotification?.(data, message.Status);
	}

	/**
	 * Handle Error Messages
	 */
	private handleErrorMessage(message: ResponseMessage): void {
		logger.error('[EventCentral] Backend error', {
			messageType: message.messageType,
			status: message.Status.status,
			description: message.Status.description,
		});

		// Invoke registered handler
		this.handlers.onError?.(message, message.Status);
	}

	/**
	 * Generate unique message ID
	 * Format: XXXX.XXXX.XXXX.XXXX.XXXX.XXXX (similar to backend format)
	 */
	//private generateMessageId(): string {
	//	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	//	const segmentLength = 4;
	//	const segmentCount = 6;
	//	const segments: string[] = [];

	//	for (let i = 0; i < segmentCount; i++) {
	//		let segment = '';
	//		for (let j = 0; j < segmentLength; j++) {
	//			segment += chars.charAt(Math.floor(Math.random() * chars.length));
	//		}
	//		segments.push(segment);
	//	}

	//	return segments.join('.');
	//}

	/**
	 * Clear all handlers
	 */
	clearHandlers(): void {
		this.handlers = {};
		logger.debug('[EventCentral] Handlers cleared');
	}

	/**
	 * Cancel pending request
	 */
	cancelRequest(messageId: string): void {
		const pending = this.pendingRequests.get(messageId);
		if (pending) {
			clearTimeout(pending.timeout);
			pending.reject(new Error('Request cancelled'));
			this.pendingRequests.delete(messageId);
			logger.debug('[EventCentral] Request cancelled', { messageId });
		}
	}
}

/**
 * Singleton instance of EventCentral
 */
export const eventCentral = new EventCentral();

export default eventCentral;
